import React, { useCallback } from "react";
import { inject, observer } from "mobx-react";
import { api } from "core/utility";
import { snackbarStore } from "library/core/stores/snackbar/SnackbarStore";
import { SnackbarVariants } from "library/core/stores/snackbar/enums";
import Modal from "library/components/modal";
import TailwindBox from "library/components/_tailwind/box";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindButton from "library/components/_tailwind/button";
import TailwindTranslatedText from "library/components/_tailwind/translated-text";
import NodeChatStore from "../_stores/nodechat/NodeChatStore";
import { IMember } from "../_stores/chat/interfaces";

interface Props {
  nodeChatStore?: NodeChatStore;
  isOpen: boolean;
  member: IMember;
  onClose: () => void;
}

const BlockMemberModal: React.FC<Props> = ({ 
  nodeChatStore, 
  isOpen, 
  member, 
  onClose 
}) => {
  const { kickMember } = nodeChatStore!;

  const handleBlockMember = useCallback(async () => {
    try {
      await api.blockMember.post({ username: member.handle });
      kickMember(member.id);
      
      snackbarStore.enqueueSnackbar({
        message: {
          id: "block-member-modal.success",
          default: "Member blocked successfully",
        },
        variant: SnackbarVariants.SUCCESS,
      });
      onClose();
    } catch (error) {
      snackbarStore.enqueueSnackbar({
        message: {
          id: "block-member-modal.error",
          default: "Failed to block member",
        },
        variant: SnackbarVariants.ERROR,
      });
    }
  }, [member, onClose, kickMember]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      open={isOpen}
      shouldCloseOnOverlayClick={true}
      showNativeCloseButton={false}
      backdropVariant='blur'
    >
      <TailwindBox
        padding={["p-6"]}
        backgroundColor={"bg-primary-bg-color"}
        width={"w-full"}
        maxWidth={"max-w-md"}
        margin={["mx-auto"]}
        borderRadius={"rounded-lg"}
      >
        <TailwindFlex flexDirection={"flex-col"} alignItems={"items-center"}>
          <TailwindTranslatedText
            textAlign={"text-center"}
            fontWeight={"font-bold"}
            fontSize={"text-lg"}
            margin={["mb-4"]}
            descriptor={{
              id: "block-member-modal.title",
              defaultMessage: "Block Member",
            }}
          />
          <TailwindTranslatedText
            textAlign={"text-center"}
            margin={["mb-6"]}
            descriptor={{
              id: "block-member-modal.confirmation",
              defaultMessage: "Do you really want to block this member?",
            }}
          />

          <TailwindFlex 
            flexDirection={"flex-row"} 
            alignItems={"items-center"}
            gap={"gap-4"}
          >
            <TailwindButton
              onClick={handleCancel}
              backgroundColor={"bg-gray-500"}
              className={["hover:bg-gray-600"]}
              padding={["px-6", "py-2"]}
              textAlign={"text-center"}
              rounded={true}
            >
              <TailwindTranslatedText
                descriptor={{
                  id: "block-member-modal.no",
                  defaultMessage: "No",
                }}
              />
            </TailwindButton>

            <TailwindButton
              onClick={handleBlockMember}
              backgroundColor={"bg-red-500"}
              className={["hover:bg-red-600"]}
              padding={["px-6", "py-2"]}
              textAlign={"text-center"}
              rounded={true}
            >
              <TailwindTranslatedText
                descriptor={{
                  id: "block-member-modal.yes",
                  defaultMessage: "Yes",
                }}
              />
            </TailwindButton>
          </TailwindFlex>
        </TailwindFlex>
      </TailwindBox>
    </Modal>
  );
};

export default inject("nodeChatStore")(observer(BlockMemberModal));
